import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { sharedQueries } from "@surgia/api/src/queryStore/shared";
import { toast } from "sonner";
import { DraftPurchaseOrder } from "../../../../../packages/api/src/shared/purchaseOrderCatalog";
import { Listbox } from "@headlessui/react";
import CreatePurchaseOrderModal from "../../containers/Modals/CreatePurchaseOrderModal";
import { permissions } from "../../permissions";

// components
import BreadCrumbs from "@surgia/ui/components/breadCrumb";
import Badge from "@surgia/ui/components/badge";
import Pagination from "@surgia/ui/containers/table/pagination";
import { Table, TableCard } from "@vctors/vctors-untitled-ui";
import Button from "@surgia/ui/components/button";
// Untitled UI components
import { Button as UntitledButton } from "@vctors/vctors-untitled-ui";

// icons
import addIcon from "@surgia/ui/icons/table/addBlack.svg";
import { HUB_PROTECTED_ROUTES } from "../../routes";
import config from "@surgia/config";

// Price Display Component
const PriceDisplay: React.FC<{
  currentPrice: number;
  originalPrice: number;
  isUnitPrice?: boolean;
}> = ({ currentPrice, originalPrice, isUnitPrice = false }) => {
  const hasDiscount =
    Math.round(originalPrice * 100) > Math.round(currentPrice * 100);

  const width = isUnitPrice ? "w-16" : "w-20";

  return (
    <div className={`${width} text-center`}>
      {hasDiscount ? (
        <div className="flex flex-col">
          <div className="text-sm text-red-500 line-through text-center">
            {originalPrice.toFixed(1)}
          </div>
          <div className="text-sm font-medium text-gray-900 text-center">
            {currentPrice.toFixed(1)}
          </div>
        </div>
      ) : (
        <div className="text-sm font-medium text-gray-900">
          {currentPrice.toFixed(1)}
        </div>
      )}
    </div>
  );
};

// Custom Product Table Component
const ProductTable: React.FC<{
  products: DraftPurchaseOrder;
  onQuantityChange: (productId: number, quantity: number) => void;
  onRemoveProduct: (productId: number) => void;
  mode: "edit" | "view";
  removingProductId?: number | null;
  updatingProductId?: number | null;
  optimisticQuantities?: Record<number, number>;
  hasEditPermission?: boolean;
}> = ({
  products,
  onQuantityChange,
  onRemoveProduct,
  mode,
  removingProductId,
  updatingProductId,
  optimisticQuantities,
  hasEditPermission,
}) => {
  return (
    <TableCard.Root className="bg-white !ring-[#E9EAEB] !ring-1 !shadow-none !border-none">
      <Table aria-label="Products" className="bg-white">
        <Table.Header className="bg-[#FAFAFA] text-[#717680]">
          <Table.Head id="product" label="Product details" />
          <Table.Head id="image" label="" />
          <Table.Head id="unitPrice" label="Unit Price" />
          <Table.Head id="quantity" label="Qty" />
          <Table.Head id="totalPrice" label="Total Price" />
        </Table.Header>

        <Table.Body>
          {products?.items?.map((product) => (
            <React.Fragment key={product?.tenant_product?.id}>
              <Table.Row className="border-t border-[#E9EAEB]">
                <Table.Cell>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">
                      {
                        product.tenant_product.catalog_info
                          .product_categories[0].category.name
                      }
                    </div>
                    <div className="font-medium text-gray-900 mb-1">
                      {product?.tenant_product?.tenant_product_name !== ""
                        ? product?.tenant_product?.tenant_product_name
                        : product?.tenant_product?.catalog_info?.name}
                    </div>
                    <div className="text-xs text-gray-600 ">
                      {product?.tenant_product.catalog_info?.feature_values
                        ?.map(
                          (feature) =>
                            `${feature.feature_Name} - ${feature.featureVariant.variant_name}`
                        )
                        .join(" - ")}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Min: {product.tenant_product.minimum_quantity} | Max:{" "}
                      {product.tenant_product.maximum_quantity}
                    </div>
                    <div className="text-xs text-gray-600 mb-1">
                      SKU: {}{" "}
                      {product?.tenant_product?.tenant_product_sku &&
                      product?.tenant_product?.tenant_product_sku !== ""
                        ? "tenant sku"
                        : "catalog sku"}
                      {product?.tenant_product?.tenant_product_sku &&
                      product?.tenant_product?.tenant_product_sku !== ""
                        ? product?.tenant_product?.tenant_product_sku
                        : product?.tenant_product?.catalog_info?.sku}
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="w-12 h-12 rounded-lg overflow-hidden">
                    <img
                      src={
                        product?.tenant_product?.catalog_info
                          ?.product_images?.[0]?.image_path
                      }
                      alt={
                        product?.tenant_product?.tenant_product_name !== ""
                          ? product?.tenant_product?.tenant_product_name
                          : product?.tenant_product?.catalog_info?.name
                      }
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <PriceDisplay
                    currentPrice={product.price_per_unit?.price}
                    originalPrice={product.tenant_product?.catalog_info?.price}
                    isUnitPrice={false}
                  />
                </Table.Cell>
                <Table.Cell>
                  <div className="w-20 relative flex flex-col ">
                    <input
                      key={`qty-${product?.tenant_product?.id}-${optimisticQuantities?.[product?.tenant_product?.id] ?? product.quantity}`}

                      disabled={!hasEditPermission}
                      type="number"
                      aria-label="Quantity"
                      defaultValue={
                        optimisticQuantities?.[
                          product?.tenant_product?.id
                        ]?.toString() ??
                        (product.quantity?.toString() || "")
                      }
                      onKeyPress={(event) => {
                        if (
                          !/[0-9]/.test(event.key) &&
                          event.key !== "Backspace" &&
                          event.key !== "Delete"
                        ) {
                          event.preventDefault();
                        }
                      }}
                      onBlur={(event) => {
                        const inputValue = event.target.value;
                        const value = parseInt(inputValue);
                        if (value == product.quantity) return;
                        if (
                          inputValue === "" ||
                          isNaN(value) ||
                          value < product.tenant_product.minimum_quantity ||
                          value > product.tenant_product.maximum_quantity
                        ) {
                          // If empty or invalid, reset to current quantity
                          // Reset input to current quantity
                          event.target.value =
                            optimisticQuantities?.[
                              product?.tenant_product?.id
                            ]?.toString() ??
                            (product.quantity?.toString() || "");
                          return;
                        }

                        // Only update if valid
                        onQuantityChange(product?.tenant_product?.id, value);
                      }}
                      min={product.tenant_product.minimum_quantity}
                      max={product.tenant_product.maximum_quantity}
                      className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        updatingProductId === product?.tenant_product?.id
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      placeholder="Qty"
                    />
                    {hasEditPermission && (
                      <div className="text-xs text-gray-500 mt-1 text-center min-h-[14px]">
                        {updatingProductId === product?.tenant_product?.id &&
                          "Updating..."}
                      </div>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <PriceDisplay
                    currentPrice={product.price.price}
                    originalPrice={
                      product?.tenant_product?.catalog_info?.price *
                      product?.quantity
                    }
                    isUnitPrice={false}
                  />
                </Table.Cell>
              </Table.Row>

              {/* Remove Button - Below the main row */}
              {mode === "edit" && hasEditPermission && (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <div className="flex justify-end">
                      <button
                        onClick={() =>
                          onRemoveProduct(product?.tenant_product?.id)
                        }
                        disabled={
                          removingProductId === product?.tenant_product?.id
                        }
                        className={`text-sm ${
                          removingProductId === product?.tenant_product?.id
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      >
                        {removingProductId === product?.tenant_product?.id
                          ? "Removing..."
                          : "Remove"}
                      </button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table>
    </TableCard.Root>
  );
};

export default function PurchaseOrderCreation({
  mode = "edit",
}: {
  mode?: "edit" | "view";
}) {
  const navigate = useNavigate();
  const authState = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();

  // Add user permissions selector
  const userPermissions = useSelector(
    (state: RootState) =>
      state.auth.globalState.user_permissions?.map(
        (permission) => permission.name
      ) || []
  );

  // Get currency from global store
  const currencyCode = authState.globalState.currency_code || "SAR";

  // Fetch draft purchase order data
  const {
    data: draftPOData,
    isLoading,
    error,
  } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getCurrentDraftPO({
      componentId: String(config.componentId),
      tenantId: Number(authState.globalState.tenant_id),
    }),
    enabled: !!(
      authState.globalState.tenant_id &&
      config.componentId
    ),
  });

  // Fetch branches from global store
  const { data: branches, isLoading: isBranchesLoading } = useQuery({
    ...sharedQueries.branch.queries.getBasicBranchData({
      tenant_id: Number(authState.globalState.tenant_id),
      component_id: config.componentId,
    }),
    staleTime: 5 * 60 * 1000,
    enabled: !!authState.globalState.tenant_id,
  });

  // State management
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null); // Change from string to number | null
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [removingProductId, setRemovingProductId] = useState<number | null>(
    null
  );
  const [updatingProductId, setUpdatingProductId] = useState<number | null>(
    null
  );
  const [optimisticQuantities, setOptimisticQuantities] = useState<
    Record<number, number>
  >({});
  const [showCreatePOModal, setShowCreatePOModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 4;
  const totalPages = Math.ceil(
    ((draftPOData as DraftPurchaseOrder)?.items?.length || 0) / ITEMS_PER_PAGE
  );
  // Add pagination logic for the products
  const getPaginatedProducts = () => {
    if (!draftPOData || "errorCode" in draftPOData) return [];

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return (
      (draftPOData as DraftPurchaseOrder).items?.slice(startIndex, endIndex) ||
      []
    );
  };

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: (params: {
      componentId: string;
      tenantId: number;
      body: { tenant_product_id: number };
    }) =>
      sharedQueries.purchaseOrder.mutations
        .deletePurchaseOrderItem(params)
        .mutationFn(),
    onSuccess: () => {
      // Clear removing state
      setRemovingProductId(null);
      // Invalidate draft PO query to refresh data
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            (keyString.includes("purchaseRequest") &&
              keyString.includes("listings")) ||
            (keyString.includes("purchase-order") &&
              keyString.includes("draft"))
          );
        },
      });
    },
    onError: (error: any) => {
      // Clear removing state on error
      setRemovingProductId(null);
      toast.error("Failed to remove item", {
        description: error?.errorMessage || "Please try again later.",
      });
    },
  });

  // Update quantity mutation
  const updateQuantityMutation = useMutation({
    mutationFn: (params: {
      componentId: string;
      tenantId: number;
      body: { tenant_product_id: number; quantity: number };
    }) =>
      sharedQueries.purchaseOrder.mutations
        .updatePurchaseOrderItemQuantity(params)
        .mutationFn(),
    onSuccess: () => {
      // Clear updating state
      setUpdatingProductId(null);
      // Invalidate draft PO query to refresh data
      queryClient.invalidateQueries({
        queryKey: [
          "purchase-order",
          "draft",
          authState.globalState.tenant_id,
          config.componentId,
        ],
      });
    },
    onError: (error: any) => {
      // Clear updating state on error
      setUpdatingProductId(null);
      toast.error("Failed to update quantity", {
        description: error?.errorMessage || "Please try again later.",
      });
    },
  });

  // Add set branch mutation
  const setBranchMutation = useMutation({
    mutationFn: async (params: {
      componentId: string;
      tenantId: string;
      branchId: string;
    }) => {
      const mutation =
        sharedQueries.purchaseOrder.mutations.setPurchaseOrderBranch(params);
      return mutation.mutationFn();
    },
    onSuccess: () => {
      // Invalidate draft PO query to refresh with branch data
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            keyString.includes("purchase-order") &&
            keyString.includes("draft")
          );
        },
      });
    },
    onError: (error: any) => {
      toast.error("Failed to set branch", {
        description: error?.errorMessage || "Please try again later.",
      });
    },
  });

  const useDebounce = (callback: Function, delay: number) => {
    const timeoutRef = useRef<NodeJS.Timeout>(null);

    return useCallback(
      (...args: any[]) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => callback(...args), delay);
      },
      [callback, delay]
    );
  };

  const debouncedUpdateQuantity = useDebounce(
    (productId: number, newQuantity: number) => {
      updateQuantityMutation.mutate({
        componentId: String(config.componentId),
        tenantId: Number(authState.globalState.tenant_id),
        body: {
          tenant_product_id: productId,
          quantity: newQuantity,
        },
      });
    },
    500
  );

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    // Optimistic update
    setOptimisticQuantities((prev) => ({
      ...prev,
      [productId]: newQuantity,
    }));

    setUpdatingProductId(productId);

    debouncedUpdateQuantity(productId, newQuantity);
  };

  const handleRemoveProduct = (productId: number) => {
    if (mode === "edit") {
      setRemovingProductId(productId);
      deleteItemMutation.mutate({
        componentId: String(config.componentId),
        tenantId: Number(authState.globalState.tenant_id),
        body: {
          tenant_product_id: productId,
        },
      });
    }
  };

  const handleAddPR = () => {
    setShowCreatePOModal(true);
  };

  // Add submit PO mutation
  const submitPOMutation = useMutation({
    ...sharedQueries.purchaseOrder.mutations.submitPurchaseOrder({
      componentId: String(config.componentId),
      tenantId: Number(authState.globalState.tenant_id),
      branchId:
        selectedBranch ||
        Number(authState.globalState.user_branches?.split(",")[0]), // Use selectedBranch directly
      body: {
        branch_id:
          selectedBranch ||
          Number(authState.globalState.user_branches?.split(",")[0]), // Use selectedBranch directly
        payment_method_id: 21,
        additional_notes: additionalNotes,
      },
    }),
    onSuccess: () => {
      toast.success("Purchase order submitted successfully!");

      // Invalidate draft PO and PO listing queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            (keyString.includes("purchase-order") &&
              keyString.includes("draft")) ||
            (keyString.includes("purchaseOrder") &&
              keyString.includes("listings"))
          );
        },
      });

      navigate(-1);
    },
    onError: (error: any) => {
      toast.error("Failed to submit purchase order", {
        description: error?.message || "Please try again later.",
      });
    },
  });

  const handleConfirmOrder = () => {
    if (!selectedBranch) {
      toast.error("Please select a branch");
      return;
    }
    submitPOMutation.mutate();
  };

  // Add function to handle branch change
  const handleBranchChange = (branch: any) => {
    setSelectedBranch(branch?.id || null);
    if (branch?.id) {
      setBranchMutation.mutate({
        componentId: String(config.componentId),
        tenantId: String(authState.globalState.tenant_id),
        branchId: String(branch.id),
      });
    }
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (
      draftPOData &&
      !("errorCode" in draftPOData) &&
      (draftPOData as any).branch?.id &&
      !selectedBranch
    ) {
      setSelectedBranch((draftPOData as any).branch.id);
    }
  }, [draftPOData, selectedBranch]);
  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading purchase order...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Failed to load purchase order data
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumbs */}
          <BreadCrumbs
            items={[
              {
                label: "Purchase Requests",
                onClick: () => navigate(HUB_PROTECTED_ROUTES.PURCHASE_REQUESTS),
                show: true,
              },
              {
                label:
                  mode === "edit"
                    ? "Edit purchase order"
                    : "View purchase order",
                show: true,
              },
            ]}
          />

          {/* Page Title and Actions */}
          <div className="flex items-center justify-between mt-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {mode === "edit"
                  ? "Edit purchase order"
                  : "View purchase order"}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {(draftPOData as DraftPurchaseOrder)?.number_of_products || 0}{" "}
                products selected
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column - Products */}
          <div className="xl:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Products Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold">Products</h2>
                    <div className="flex gap-2">
                      <Badge variant="default" className="!rounded-sm bg-white">
                        {getPaginatedProducts().length} total
                      </Badge>
                      <Badge variant="default" className="!rounded-sm bg-white">
                        {(draftPOData as DraftPurchaseOrder)
                          ?.number_of_products || 0}{" "}
                        items
                      </Badge>
                    </div>
                  </div>
                  {mode === "edit" && (
                    <Button
                      variant="white"
                      startIcon={addIcon}
                      onClick={handleAddPR}
                    >
                      Add PR
                    </Button>
                  )}
                </div>
              </div>

              {/* Products Table */}
              {draftPOData && !("errorCode" in draftPOData) && (
                <ProductTable
                  hasEditPermission={userPermissions.includes(
                    permissions.EDIT_DRAFT_PURCHASE_ORDER
                  )}
                  optimisticQuantities={optimisticQuantities}
                  products={{
                    ...(draftPOData as DraftPurchaseOrder),
                    items: getPaginatedProducts(),
                  }}
                  onQuantityChange={handleQuantityChange}
                  onRemoveProduct={handleRemoveProduct}
                  mode={mode}
                  removingProductId={removingProductId}
                  updatingProductId={updatingProductId}
                />
              )}

              {/* Pagination */}
              <Pagination
                currentPage={currentPage - 1} // Pagination component uses 0-based indexing
                totalPages={totalPages}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                onPageChange={(page) => handlePageChange(page + 1)} // Convert back to 1-based
              />
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Summary</h2>

              {/* Cost Breakdown */}
              {setBranchMutation.isPending ? (
                <div className="space-y-3 mb-6 animate-pulse">
                  {/* Sub total skeleton */}
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  {/* Included discount skeleton */}
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  {/* Sub total discount skeleton */}
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  {/* Shipping fees skeleton */}
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  {/* VAT skeleton */}
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  {/* Total skeleton */}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between">
                      <div className="h-5 bg-gray-200 rounded w-16"></div>
                      <div className="h-5 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sub total</span>
                      <span>
                        {" "}
                        {(draftPOData as any)?.order_summary?.subtotal.toFixed(
                          0
                        )}{" "}
                        {currencyCode}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Included discount</span>
                      <span>
                        {(
                          draftPOData as any
                        )?.order_summary?.included_discount.toFixed(1)}{" "}
                        {currencyCode}
                      </span>
                    </div>
                    {+(
                      draftPOData as any
                    )?.order_summary?.subtotal_discount.toFixed(1) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Sub total discount</span>
                        <span>
                          {(
                            draftPOData as any
                          )?.order_summary?.subtotal_discount.toFixed(1)}{" "}
                          {currencyCode}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping fees</span>
                      <span>
                        {(draftPOData as any)?.order_summary?.shipping_fees.toFixed(
                          0
                        )}{" "}
                        {currencyCode}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        VAT ({(draftPOData as any)?.order_summary?.VAT_percentage?.toFixed(1) ?? 0}%)
                      </span>
                      <span>
                        {(draftPOData as any)?.order_summary?.VAT?.toFixed(1) ?? 0} {currencyCode}
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Total</span>
                        <span>
                          {(draftPOData as any)?.order_summary?.total.toFixed(1)}{" "}
                          {currencyCode}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Branch Selection with Untitled UI Select */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch <span className="text-[#0033CC]">*</span>
                </label>
                <Listbox
                  value={
                    branches?.find(
                      (branch: any) => +branch.id === +selectedBranch!
                    ) || null
                  }
                  onChange={handleBranchChange}
                >
                  <div className="relative">
                    <Listbox.Button
                      disabled={
                        isBranchesLoading ||
                        !userPermissions.includes(
                          permissions.EDIT_DRAFT_PURCHASE_ORDER
                        ) ||
                        setBranchMutation.isPending
                      }
                      className="w-full px-3 py-2 border border-[#D5D7DA] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5D7DA] bg-white text-left"
                    >
                      <span className="block truncate">
                        {isBranchesLoading
                          ? "Loading branches..."
                          : branches.find(
                              (branch: any) => branch.id === selectedBranch
                            )?.name || "Select branch"}
                      </span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </Listbox.Button>

                    <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                      {branches?.map((branch: any) => (
                        <Listbox.Option
                          key={branch.id}
                          value={branch}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active
                                ? "bg-blue-100 text-blue-900"
                                : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                              >
                                {branch.name}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <svg
                                    className="w-5 h-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>
              </div>

              {/* Additional Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional notes
                </label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Leave any notes about your order here"
                  className="w-full px-3 py-2 border border-[#D5D7DA] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D5D7DA] h-24 resize-none focus:ring-0"
                  maxLength={300}
                  disabled={
                    mode === "view" ||
                    !userPermissions.includes(
                      permissions.EDIT_DRAFT_PURCHASE_ORDER
                    )
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  {300 - additionalNotes.length} characters left
                </p>
              </div>

              {/* Confirm Order Button - Only show in edit mode */}
              {mode === "edit" && (
                <UntitledButton
                  onClick={handleConfirmOrder}
                  className={`w-full !bg-[#0033CC] !border-none !outline-none !ring-0 !shadow-none [&::before]:!border-none [&::before]:!content-none rounded-md ${
                    !selectedBranch ||
                    submitPOMutation.isPending ||
                    !userPermissions.includes(
                      permissions.EDIT_DRAFT_PURCHASE_ORDER
                    ) ||
                    !(
                      draftPOData &&
                      !("errorCode" in draftPOData) &&
                      draftPOData?.number_of_products
                    )
                      ? "!opacity-50 !cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                  isLoading={submitPOMutation.isPending}
                  showTextWhileLoading
                >
                  {submitPOMutation.isPending
                    ? "Submitting..."
                    : "Confirm order"}
                </UntitledButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        open={showCreatePOModal}
        onClose={() => setShowCreatePOModal(false)}
        selectedBranch={authState.globalState.user_branches}
        allBranchesIds={authState.globalState.user_branches!}
      />
    </div>
  );
}
